/** Assembling and presenting route documents. */

import type { DirectionsResult } from './directions.js';
import { newRouteId } from './id.js';
import { decodePolyline } from './polyline.js';
import {
  DEFAULT_ROUTE_OPTIONS,
  ROUTE_SCHEMA,
  type DraftRoute,
  type LngLat,
  type RouteDoc,
  DEFAULT_PROFILE,
} from './types.js';

export function emptyDraft(): DraftRoute {
  return {
    name: 'Untitled route',
    notes: '',
    profile: DEFAULT_PROFILE,
    options: { ...DEFAULT_ROUTE_OPTIONS },
    waypoints: [],
  };
}

export function buildRouteDoc(
  draft: DraftRoute,
  result: DirectionsResult,
  existing?: Pick<RouteDoc, 'id' | 'createdAt' | 'revision'>,
): RouteDoc {
  const now = Date.now();
  return {
    schema: ROUTE_SCHEMA,
    id: existing?.id ?? newRouteId(),
    name: draft.name.trim() || 'Untitled route',
    ...(draft.notes?.trim() ? { notes: draft.notes.trim() } : {}),
    profile: draft.profile,
    options: { ...draft.options },
    waypoints: draft.waypoints.map((w) => ({ ...w })),
    geometry: result.geometry,
    distance: result.distance,
    duration: result.duration,
    legs: result.legs,
    bbox: result.bbox,
    ...(result.speedLimits.length > 0 ? { speedLimits: result.speedLimits } : {}),
    ...(result.maneuvers.length > 0 ? { maneuvers: result.maneuvers } : {}),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    revision: (existing?.revision ?? 0) + 1,
  };
}

export function routeCoordinates(route: RouteDoc): LngLat[] {
  return decodePolyline(route.geometry);
}

export function routeToGeoJson(route: RouteDoc) {
  return {
    type: 'Feature' as const,
    properties: { id: route.id, name: route.name },
    geometry: {
      type: 'LineString' as const,
      coordinates: decodePolyline(route.geometry),
    },
  };
}

/** UK/Europe: miles for the UK crowd, km everywhere else. Driver's choice. */
export type DistanceUnit = 'mi' | 'km';

export function formatDistance(meters: number, unit: DistanceUnit = 'mi'): string {
  if (unit === 'km') {
    const km = meters / 1000;
    return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  }
  const miles = meters / 1609.344;
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

/**
 * What time you get there, as a clock time.
 *
 * "3 hr 54 min" is a fact about the road; "arrives 11:27" is the one people
 * actually act on — whether to stop for lunch, whether to tell someone you are
 * running late. Both are shown, because the remaining time is what changes as
 * you drive and the arrival time is what you were planning against.
 *
 * Deliberately not seconds-accurate: it is derived from an average pace over
 * the whole route, and a time to the minute already claims more than it knows.
 */
export function formatArrival(secondsFromNow: number, now: Date = new Date()): string {
  const at = new Date(now.getTime() + Math.max(0, secondsFromNow) * 1000);
  const hh = String(at.getHours()).padStart(2, '0');
  const mm = String(at.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function formatDuration(seconds: number): string {
  const total = Math.round(seconds / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}
